import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import GenericDraggableTableView from "../../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../../Helper/DigitalTwin/DataFile";
import { usePagesContent } from "../../../../Hooks/usePagesContent";

const StateOfUseDrillDownTable = ({
  tableConfigs,
  getTableData,
  applyTableAction,
  getCSVFileName,
  selectedDataPoint,
  pageSize = 10,
  customRenderers = {},
  isOpen = true,
  showColumnSettings = true,
}) => {
  const pagesContent = usePagesContent();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [sortBy, setSortBy] = useState([]);

  const [initialColumns, setInitialColumns] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
  const [defaultSelectedColumnKeys, setDefaultSelectedColumnKeys] = useState([]);
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [columnOrder, setColumnOrder] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({});

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTableData([]);
      setPageNumber(1);
      setRowsPerPage(pageSize);
      setTotalSelectedRowKeys(new Set());
      setIsSelectAll(false);
      setSortBy([]);
      setSelectedFilters({});
    }
  }, [isOpen, pageSize]);

  // Reset pagination when selected data point changes
  useEffect(() => {
    if (isOpen && selectedDataPoint) {
      setPageNumber(1);
      setSortBy([]);
    }
  }, [selectedDataPoint, isOpen]);

  // Build columns from table config
  useEffect(() => {
    if (!tableConfigs?.tableColumnsConfig || !tableConfigs?.tableColumnOrder) return;

    const availableColumns = getColumnsConfig({
      tableColumns: tableConfigs.tableColumnsConfig,
      tableColumnOrder: tableConfigs.tableColumnOrder,
      customRenderers,
    });

    setInitialColumns(availableColumns);
    const defaultSelected = availableColumns.slice(0, MAX_COLUMN_SELECTION_COUNT);
    const defaultKeys = defaultSelected.map((col) => col.dataIndex);
    setTableColumns(defaultSelected);
    setSelectedColumnKeys(defaultKeys);
    setDefaultSelectedColumnKeys(defaultKeys);
    setColumnOrder(defaultSelected.map((col) => col.headerLabel));
  }, [tableConfigs, customRenderers]);

  // Fetch table data
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !getTableData) return;
      try {
        setIsLoading(true);
        const result = await getTableData(selectedDataPoint, pageNumber, sortBy);
        setTableData(result.data || []);
        setTotal(result.total || 0);
      } catch (error) {
        console.error("Error fetching table data:", error);
        setTableData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [isOpen, getTableData, pageNumber, selectedDataPoint, sortBy]);

  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  const handleSortChange = (key, order) => {
    let columnKey = key;
    if (tableConfigs?.tableColumnsConfig?.[key]?.sortKey) {
      columnKey = tableConfigs.tableColumnsConfig[key].sortKey;
    }
    setSortBy([{ column: columnKey, direction: order === "asc" ? "ASC" : "DESC" }]);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const getCurrentSorting = () => {
    if (sortBy.length > 0) {
      return { sortBy: sortBy[0].column, orderBy: sortBy[0].direction.toLowerCase() };
    }
    return {};
  };

  const onSelectChange = (record, selected) => {
    const updated = new Set(totalSelectedRowKeys);
    if (selected) updated.add(record.id);
    else {
      updated.delete(record.id);
      if (isSelectAll) setIsSelectAll(false);
    }
    setTotalSelectedRowKeys(updated);
  };

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(selected ? new Set(selectedRows.map((r) => r?.id)) : new Set());
  };

  const onColumnOrderChange = (newTableColumns) => {
    setColumnOrder(newTableColumns.map((col) => col.headerLabel));
  };

  const applySelectedColumns = (newSelectedKeys) => {
    const filtered = initialColumns.filter((col) => newSelectedKeys.includes(col.dataIndex));
    setTableColumns(filtered);
    setSelectedColumnKeys(newSelectedKeys);
    setColumnOrder(filtered.map((col) => col.headerLabel));
  };

  const handleClearSelection = () => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const getCSVData = async () => {
    if (!applyTableAction) return [];
    return await applyTableAction(Array.from(totalSelectedRowKeys), "download", isSelectAll, {
      columns: tableColumns,
      columnOrder,
    });
  };

  const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
    if (!applyTableAction) return;
    return await applyTableAction(Array.from(totalSelectedRowKeys), "mail", isSelectAll, {
      columns: tableColumns,
      columnOrder,
      emailTo: sendTo,
      emailCc: sendCc,
      subject,
      body,
    });
  };

  const tableTitle =
    typeof tableConfigs?.title === "function"
      ? tableConfigs.title(selectedDataPoint)
      : tableConfigs?.title || "Table";

  const tableFilters = tableConfigs?.filters || {};
  const hasFilters = Object.keys(tableFilters).length > 0;

  return (
    <Box sx={{ flex: 1 }}>
      <GenericDraggableTableView
        title={tableTitle}
        showPrimaryButton={false}
        count={total || tableData.length}
        tableColumns={tableColumns}
        selectedColumns={selectedColumnKeys}
        tableData={tableData}
        availableEmailDomains={pagesContent?.metaInfo?.globalConfig?.["email.domains"] || []}
        initialColumns={initialColumns}
        applySelectedColumns={applySelectedColumns}
        defaultSelectedColumns={defaultSelectedColumnKeys}
        isLoading={isLoading}
        disablePagination={false}
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        total={total !== undefined ? total : tableData.length}
        disableSearch={true}
        filters={tableFilters}
        selectedFilters={selectedFilters}
        applyFilters={setSelectedFilters}
        disableFiltersSettings={!hasFilters}
        disableColumnSettings={!showColumnSettings}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        applySort={handleSortChange}
        initialSorting={getCurrentSorting()}
        emptyMessage={tableConfigs?.emptyMessage || "No data available"}
        containerStyles={{
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
        }}
        getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
        getTotalRowSelectionCount={() =>
          isSelectAll ? (total || tableData.length) : totalSelectedRowKeys.size
        }
        rowSelection={{
          selectedRowKeys: Array.from(totalSelectedRowKeys),
          onSelect: onSelectChange,
          onSelectAll: onSelectAllChange,
        }}
        onClearSelection={handleClearSelection}
        onPageChange={handlePageChange}
        getCSVData={getCSVData}
        getCSVFileName={getCSVFileName}
        onSendEmail={onSendEmail}
      />
    </Box>
  );
};

export default StateOfUseDrillDownTable;
